import { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'

import type { AdaptiveCardChoice } from '../../store/slices/adaptiveCardSlice'
import { BaseAgentData, CustomMenu } from '../../models/types'
import { DetailedAttachmentAction, RetorikActivity } from '../../models/activityTypes'
import { Mode, RecognitionState } from '../../models/enums'

interface InputOutputHandlerProps {
  instanceId?: string
}

const InputOutputHandler = ({ instanceId }: InputOutputHandlerProps): null => {
  const dispatch = useDispatch()
  const isMicrophoneAllowed = useSelector((state: RootState) => state.speechReducer.isMicrophoneAllowed)
  const isMicrophoneAllowedRef = useRef<boolean>(isMicrophoneAllowed)

  const handleCustomEvent = (event: CustomEvent, method: any): void => {
    // Check if an instance id is given to use this method in a single instance of Retorik Framework when there are several
    const checkInstanceId = event.detail?.instanceId ? (instanceId ? event.detail.instanceId === instanceId : false) : true

    if (checkInstanceId) {
      try {
        event.detail?.values?.length ? dispatch(method(event.detail.values[0])) : dispatch(method())
      } catch (e) {
        console.error(`Retorik Framework > failed to use ${method}`)
      }
    }
  }

  useEffect(() => {
    isMicrophoneAllowedRef.current = isMicrophoneAllowed
  }, [isMicrophoneAllowed])

  // Use refs to get updated states in native javascript events
  const handleToggleMicrophone = () => {
    isMicrophoneAllowedRef.current ? toggleMicrophone() : dispatch(storeActions.speech.askPermission())
  }

  useEffect(() => {
    // Widget store events
    document.addEventListener('retorik_openWidget', ((e: CustomEvent) => handleCustomEvent(e, storeActions.widget.openWidget)) as EventListener)
    document.addEventListener('retorik_closeWidget', ((e: CustomEvent) => handleCustomEvent(e, storeActions.widget.closeWidget)) as EventListener)
    document.addEventListener('retorik_toggleWidget', ((e: CustomEvent) => handleCustomEvent(e, storeActions.widget.toggleWidget)) as EventListener)
    document.addEventListener('retorik_toggleDisplay', ((e: CustomEvent) => handleCustomEvent(e, storeActions.widget.toggleDisplay)) as EventListener)

    // Utils store events
    document.addEventListener('retorik_showAgent', ((e: CustomEvent) => handleCustomEvent(e, storeActions.utils.showAgent)) as EventListener)
    document.addEventListener('retorik_hideAgent', ((e: CustomEvent) => handleCustomEvent(e, storeActions.utils.hideAgent)) as EventListener)
    document.addEventListener('retorik_resetUtilsStore', ((e: CustomEvent) => handleCustomEvent(e, storeActions.utils.resetUtilsStore)) as EventListener)
    document.addEventListener('retorik_lockClickedButton', ((e: CustomEvent) => handleCustomEvent(e, storeActions.utils.lockClickedButton)) as EventListener)
    document.addEventListener('retorik_setCurrentCustomView', ((e: CustomEvent) => handleCustomEvent(e, storeActions.utils.setCurrentCustomView)) as EventListener)
    document.addEventListener('retorik_setCurrentSubView', ((e: CustomEvent) => handleCustomEvent(e, storeActions.utils.setCurrentSubView)) as EventListener)
    document.addEventListener('retorik_setModalFullscreenContent', ((e: CustomEvent) => handleCustomEvent(e, storeActions.utils.setModalFullscreenContent)) as EventListener)
    document.addEventListener('retorik_addDetailedAttachmentTrigger', ((e: CustomEvent) => handleCustomEvent(e, storeActions.utils.addDetailedAttachmentTrigger)) as EventListener)
    document.addEventListener('retorik_setDashboardOpened', ((e: CustomEvent) => handleCustomEvent(e, storeActions.utils.setDashboardOpened)) as EventListener)

    // Locale store events
    document.addEventListener('retorik_setLocale', ((e: CustomEvent) => handleCustomEvent(e, storeActions.locale.setLocale)) as EventListener)
    document.addEventListener('retorik_resetLocaleStore', ((e: CustomEvent) => handleCustomEvent(e, storeActions.locale.resetLocaleStore)) as EventListener)
    document.addEventListener('retorik_addDumpedLanguageFallback', ((e: CustomEvent) => handleCustomEvent(e, storeActions.locale.addDumpedLanguageFallback)) as EventListener)
    document.addEventListener('retorik_addReplacementLocale', ((e: CustomEvent) => handleCustomEvent(e, storeActions.locale.addReplacementLocale)) as EventListener)

    // Adaptive cards store events
    document.addEventListener('retorik_setValue', ((e: CustomEvent) => handleCustomEvent(e, storeActions.adaptiveCard.setValue)) as EventListener)
    document.addEventListener('retorik_setCurrentId', ((e: CustomEvent) => handleCustomEvent(e, storeActions.adaptiveCard.setCurrentId)) as EventListener)
    document.addEventListener('retorik_setToggleValue', ((e: CustomEvent) => handleCustomEvent(e, storeActions.adaptiveCard.setToggleValue)) as EventListener)
    document.addEventListener('retorik_addCurrentToggleId', ((e: CustomEvent) => handleCustomEvent(e, storeActions.adaptiveCard.addCurrentToggleId)) as EventListener)
    document.addEventListener('retorik_resetAdaptiveCardStore', ((e: CustomEvent) => handleCustomEvent(e, storeActions.adaptiveCard.resetAdaptiveCardStore)) as EventListener)

    // View store utils
    document.addEventListener('retorik_setVisibility', ((e: CustomEvent) => handleCustomEvent(e, storeActions.view.setVisibility)) as EventListener)
    document.addEventListener('retorik_toggleVisibility', ((e: CustomEvent) => handleCustomEvent(e, storeActions.view.toggleVisibility)) as EventListener)

    // Speech store utils
    document.addEventListener('retorik_setActiveRecognitionState', ((e: CustomEvent) => handleCustomEvent(e, storeActions.speech.setActiveRecognitionState)) as EventListener)
    document.addEventListener('retorik_cancelCurrentUtterance', ((e: CustomEvent) => handleCustomEvent(e, storeActions.speech.cancelCurrentUtterance)) as EventListener)
    document.addEventListener('retorik_toggleMicrophone', ((e: CustomEvent) => handleCustomEvent(e, storeActions.speech.toggleMicrophone)) as EventListener)
    document.addEventListener('retorik_setMuted', ((e: CustomEvent) => handleCustomEvent(e, storeActions.speech.setMuted)) as EventListener)
    document.addEventListener('retorik_setMode', ((e: CustomEvent) => handleCustomEvent(e, storeActions.speech.setMode)) as EventListener)
    document.addEventListener('retorik_playExternalSynthesis', ((e: CustomEvent) => handleCustomEvent(e, storeActions.speech.playExternalSynthesis)) as EventListener)
    document.addEventListener('retorik_resetSpeechStore', ((e: CustomEvent) => handleCustomEvent(e, storeActions.speech.resetSpeechStore)) as EventListener)

    // Retorik store utils
    document.addEventListener('retorik_setDisplaySubtitles', ((e: CustomEvent) => handleCustomEvent(e, storeActions.retorik.setDisplaySubtitles)) as EventListener)
    document.addEventListener('retorik_toggleSubtitles', ((e: CustomEvent) => handleCustomEvent(e, storeActions.retorik.toggleSubtitles)) as EventListener)
    document.addEventListener('retorik_setAgentData', ((e: CustomEvent) => handleCustomEvent(e, storeActions.retorik.setAgentData)) as EventListener)
    document.addEventListener('retorik_setLoaderClosed', ((e: CustomEvent) => handleCustomEvent(e, storeActions.retorik.setLoaderClosed)) as EventListener)
    document.addEventListener('retorik_resetRetorikStore', ((e: CustomEvent) => handleCustomEvent(e, storeActions.retorik.resetRetorikStore)) as EventListener)

    // Activity store utils
    document.addEventListener('retorik_resetActivityStore', ((e: CustomEvent) => handleCustomEvent(e, storeActions.activity.resetActivityStore)) as EventListener)

    // Directline store utils
    document.addEventListener('retorik_sendMessage', ((e: CustomEvent) => handleCustomEvent(e, storeActions.directline.sendMessage)) as EventListener)
    document.addEventListener('retorik_sendEvent', ((e: CustomEvent) => handleCustomEvent(e, storeActions.directline.sendEvent)) as EventListener)
    document.addEventListener('retorik_pauseConversation', ((e: CustomEvent) => handleCustomEvent(e, storeActions.directline.pauseConversation)) as EventListener)
    document.addEventListener('retorik_resumeConversation', ((e: CustomEvent) => handleCustomEvent(e, storeActions.directline.resumeConversation)) as EventListener)
    document.addEventListener('retorik_sendMessageToChatbot', ((e: CustomEvent) => handleCustomEvent(e, storeActions.directline.sendMessageToChatbot)) as EventListener)
    document.addEventListener('retorik_addExternalActivityHandler', ((e: CustomEvent) => handleCustomEvent(e, storeActions.directline.addExternalActivityHandler)) as EventListener)
    document.addEventListener('retorik_setExternalActivityHandlers', ((e: CustomEvent) => handleCustomEvent(e, storeActions.directline.setExternalActivityHandlers)) as EventListener)
    document.addEventListener('retorik_resetDirectlineStore', ((e: CustomEvent) => handleCustomEvent(e, storeActions.directline.resetDirectlineStore)) as EventListener)

    return () => {
      document.removeEventListener('retorik_openWidget', ((e: CustomEvent) => handleCustomEvent(e, openWidget)) as EventListener)
      document.removeEventListener('retorik_closeWidget', ((e: CustomEvent) => handleCustomEvent(e, closeWidget)) as EventListener)
      document.removeEventListener('retorik_toggleWidget', ((e: CustomEvent) => handleCustomEvent(e, toggleWidget)) as EventListener)
      document.removeEventListener('retorik_toggleDisplay', ((e: CustomEvent) => handleCustomEvent(e, toggleDisplay)) as EventListener)

      document.removeEventListener('retorik_showAgent', ((e: CustomEvent) => handleCustomEvent(e, showAgent)) as EventListener)
      document.removeEventListener('retorik_hideAgent', ((e: CustomEvent) => handleCustomEvent(e, hideAgent)) as EventListener)
      document.removeEventListener('retorik_resetUtilsStore', ((e: CustomEvent) => handleCustomEvent(e, resetUtilsStore)) as EventListener)
      document.removeEventListener('retorik_lockClickedButton', ((e: CustomEvent) => handleCustomEvent(e, lockClickedButton)) as EventListener)
      document.removeEventListener('retorik_setCurrentCustomView', ((e: CustomEvent) => handleCustomEvent(e, setCurrentCustomView)) as EventListener)
      document.removeEventListener('retorik_setCurrentSubView', ((e: CustomEvent) => handleCustomEvent(e, setCurrentSubView)) as EventListener)
      document.removeEventListener('retorik_sendActivity', ((e: CustomEvent) => handleCustomEvent(e, sendActivity)) as EventListener)
      document.removeEventListener('retorik_setModalFullscreenContent', ((e: CustomEvent) => handleCustomEvent(e, setModalFullscreenContent)) as EventListener)
      document.removeEventListener('retorik_addDetailedAttachmentTrigger', ((e: CustomEvent) => handleCustomEvent(e, addDetailedAttachmentTrigger)) as EventListener)
      document.removeEventListener('retorik_setDashboardOpened', ((e: CustomEvent) => handleCustomEvent(e, setDashboardOpened)) as EventListener)

      document.removeEventListener('retorik_setLocale', ((e: CustomEvent) => handleCustomEvent(e, setLocale)) as EventListener)
      document.removeEventListener('retorik_resetLocaleStore', ((e: CustomEvent) => handleCustomEvent(e, resetLocaleStore)) as EventListener)
      document.removeEventListener('retorik_addDumpedLanguageFallback', ((e: CustomEvent) => handleCustomEvent(e, addDumpedLanguageFallback)) as EventListener)
      document.removeEventListener('retorik_addReplacementLocale', ((e: CustomEvent) => handleCustomEvent(e, addReplacementLocale)) as EventListener)

      document.removeEventListener('retorik_setValue', ((e: CustomEvent) => handleCustomEvent(e, setValue)) as EventListener)
      document.removeEventListener('retorik_setCurrentId', ((e: CustomEvent) => handleCustomEvent(e, setCurrentId)) as EventListener)
      document.removeEventListener('retorik_setToggleValue', ((e: CustomEvent) => handleCustomEvent(e, setToggleValue)) as EventListener)
      document.removeEventListener('retorik_addCurrentToggleId', ((e: CustomEvent) => handleCustomEvent(e, addCurrentToggleId)) as EventListener)
      document.removeEventListener('retorik_resetAdaptiveCardStore', ((e: CustomEvent) => handleCustomEvent(e, resetAdaptiveCardStore)) as EventListener)

      document.removeEventListener('retorik_setVisibility', ((e: CustomEvent) => handleCustomEvent(e, setVisibility)) as EventListener)
      document.removeEventListener('retorik_toggleVisibility', ((e: CustomEvent) => handleCustomEvent(e, toggleVisibility)) as EventListener)

      document.removeEventListener('retorik_setActiveRecognitionState', ((e: CustomEvent) => handleCustomEvent(e, setActiveRecognitionState)) as EventListener)
      document.removeEventListener('retorik_cancelCurrentUtterance', ((e: CustomEvent) => handleCustomEvent(e, cancelCurrentUtterance)) as EventListener)
      document.removeEventListener('retorik_toggleMicrophone', handleToggleMicrophone)
      document.removeEventListener('retorik_setMuted', ((e: CustomEvent) => handleCustomEvent(e, setMuted)) as EventListener)
      document.removeEventListener('retorik_playExternalSynthesis', ((e: CustomEvent) => handleCustomEvent(e, playExternalSynthesis)) as EventListener)
      document.removeEventListener('retorik_resetSpeechStore', ((e: CustomEvent) => handleCustomEvent(e, resetSpeechStore)) as EventListener)

      document.removeEventListener('retorik_setDisplaySubtitles', ((e: CustomEvent) => handleCustomEvent(e, setDisplaySubtitles)) as EventListener)
      document.removeEventListener('retorik_toggleSubtitles', ((e: CustomEvent) => handleCustomEvent(e, toggleSubtitles)) as EventListener)
      document.removeEventListener('retorik_setAgentData', ((e: CustomEvent) => handleCustomEvent(e, setAgentData)) as EventListener)
      document.removeEventListener('retorik_setMode', ((e: CustomEvent) => handleCustomEvent(e, setMode)) as EventListener)
      document.removeEventListener('retorik_setLoaderClosed', ((e: CustomEvent) => handleCustomEvent(e, setLoaderClosed)) as EventListener)
      document.removeEventListener('retorik_resetRetorikStore', ((e: CustomEvent) => handleCustomEvent(e, resetRetorikStore)) as EventListener)

      document.removeEventListener('retorik_resetActivityStore', ((e: CustomEvent) => handleCustomEvent(e, resetActivityStore)) as EventListener)

      document.removeEventListener('retorik_sendMessage', ((e: CustomEvent) => handleCustomEvent(e, sendMessage)) as EventListener)
      document.removeEventListener('retorik_sendEvent', ((e: CustomEvent) => handleCustomEvent(e, sendEvent)) as EventListener)
      document.removeEventListener('retorik_pauseConversation', ((e: CustomEvent) => handleCustomEvent(e, pauseConversation)) as EventListener)
      document.removeEventListener('retorik_resumeConversation', ((e: CustomEvent) => handleCustomEvent(e, resumeConversation)) as EventListener)
      document.removeEventListener('retorik_sendMessageToChatbot', ((e: CustomEvent) => handleCustomEvent(e, sendMessageToChatbot)) as EventListener)
      document.removeEventListener('retorik_addExternalActivityHandler', ((e: CustomEvent) => handleCustomEvent(e, addExternalActivityHandler)) as EventListener)
      document.removeEventListener('retorik_setExternalActivityHandlers', ((e: CustomEvent) => handleCustomEvent(e, setExternalActivityHandlers)) as EventListener)
      document.removeEventListener('retorik_resetDirectlineStore', ((e: CustomEvent) => handleCustomEvent(e, resetDirectlineStore)) as EventListener)
    }
  }, [])

  return null
}

export default InputOutputHandler

const eventDispatch = (name: string, values: Array<any>, instanceId?: string): void => {
  document.dispatchEvent(
    new CustomEvent(name, {
      detail: {
        instanceId: instanceId,
        values: values
      }
    })
  )
}

// Widget utils
const openWidget = (instanceId?: string) => eventDispatch('retorik_openWidget', [], instanceId)
const closeWidget = (instanceId?: string) => eventDispatch('retorik_closeWidget', [], instanceId)
const toggleWidget = (instanceId?: string) => eventDispatch('retorik_toggleWidget', [], instanceId)
const toggleDisplay = (instanceId?: string) => eventDispatch('retorik_toggleDisplay', [], instanceId)

// Utils utils
const showAgent = (instanceId?: string) => eventDispatch('retorik_showAgent', [], instanceId)
const hideAgent = (instanceId?: string) => eventDispatch('retorik_hideAgent', [], instanceId)
const resetUtilsStore = (instanceId?: string) => eventDispatch('retorik_resetUtilsStore', [], instanceId)
const lockClickedButton = (instanceId?: string) => eventDispatch('retorik_lockClickedButton', [], instanceId)
const setCurrentCustomView = (value: CustomMenu, instanceId?: string) => eventDispatch('retorik_setCurrentCustomView', [value], instanceId)
const setCurrentSubView = (value: number, instanceId?: string) => eventDispatch('retorik_setCurrentSubView', [value], instanceId)
/**
 * deprecated since v3.2.6
 */
const sendActivity = (value: RetorikActivity | undefined, instanceId?: string) => eventDispatch('retorik_sendActivity', [value], instanceId)
const addDetailedAttachmentTrigger = (value: DetailedAttachmentAction, instanceId?: string) => eventDispatch('retorik_addDetailedAttachmentTrigger', [value], instanceId)
const setModalFullscreenContent = (value: JSX.Element | null, instanceId?: string) => eventDispatch('retorik_setModalFullscreenContent', [value], instanceId)
const setDashboardOpened = (value: boolean, instanceId?: string) => eventDispatch('retorik_setDashboardOpened', [value], instanceId)

// Locale utils
const setLocale = (value: string, instanceId?: string) => eventDispatch('retorik_setLocale', [value], instanceId)
const resetLocaleStore = (instanceId?: string) => eventDispatch('retorik_resetLocaleStore', [], instanceId)
const addDumpedLanguageFallback = (key: string, value: string, instanceId?: string) => eventDispatch('retorik_addDumpedLanguageFallback', [{ key: key, value: value }], instanceId)
const addReplacementLocale = (key: string, value: string, instanceId?: string) => eventDispatch('retorik_addReplacementLocale', [{ key: key, value: value }], instanceId)

// Adaptive cards utils
const setValue = (id: string, value: string | number | Array<string> | Array<number> | AdaptiveCardChoice | boolean) => eventDispatch('retorik_setValue', [{ id: id, value: value }], undefined)
const setCurrentId = (value: string | undefined) => eventDispatch('retorik_setCurrentId', [value], undefined)
const setToggleValue = (id: string, value: boolean) => eventDispatch('retorik_setToggleValue', [{ id: id, value: value }], undefined)
const addCurrentToggleId = (id: string, defaultValue: boolean) => eventDispatch('retorik_addCurrentToggleId', [{ id: id, defaultValue: defaultValue }], undefined)
const resetAdaptiveCardStore = (instanceId?: string) => eventDispatch('retorik_resetAdaptiveCardStore', [], instanceId)

// View utils
const setVisibility = (value: string, instanceId?: string) => eventDispatch('retorik_setVisibility', [value], instanceId)
const toggleVisibility = (instanceId?: string) => eventDispatch('retorik_toggleVisibility', [], instanceId)

// Speech utils
const setActiveRecognitionState = (value: RecognitionState, instanceId?: string) => eventDispatch('retorik_setActiveRecognitionState', [value], instanceId)
const cancelCurrentUtterance = (instanceId?: string) => eventDispatch('retorik_cancelCurrentUtterance', [], instanceId)
const toggleMicrophone = (instanceId?: string) => eventDispatch('retorik_toggleMicrophone', [], instanceId)
const setMuted = (value: boolean, instanceId?: string) => eventDispatch('retorik_setMuted', [value], instanceId)
const playExternalSynthesis = (value: string, instanceId?: string) => eventDispatch('retorik_playExternalSynthesis', [value], instanceId)
const resetSpeechStore = (instanceId?: string) => eventDispatch('retorik_resetSpeechStore', [], instanceId)

// Retorik utils
const setDisplaySubtitles = (value: boolean, instanceId?: string) => eventDispatch('retorik_setDisplaySubtitles', [value], instanceId)
const toggleSubtitles = (instanceId?: string) => eventDispatch('retorik_toggleSubtitles', [], instanceId)
const setAgentData = (value: BaseAgentData, instanceId?: string) => eventDispatch('retorik_setAgentData', [value], instanceId)
const setMode = (value: Mode, instanceId?: string) => eventDispatch('retorik_setMode', [value], instanceId)
const setLoaderClosed = (value: boolean, instanceId?: string) => eventDispatch('retorik_setLoaderClosed', [value], instanceId)
const resetRetorikStore = (instanceId?: string) => eventDispatch('retorik_resetRetorikStore', [], instanceId)

// Activity utils
const resetActivityStore = (instanceId?: string) => eventDispatch('retorik_resetActivityStore', [], instanceId)

// Directline utils
const sendMessage = (text?: string, value?: Record<string, any>, instanceId?: string) => eventDispatch('retorik_sendMessage', [{ text: text, value: value }], instanceId)
const sendEvent = (name: string, value?: Record<string, any> | null, instanceId?: string) => eventDispatch('retorik_sendEvent', [{ name: name, value: value }], instanceId)
const pauseConversation = (instanceId?: string) => eventDispatch('retorik_pauseConversation', [], instanceId)
const resumeConversation = (instanceId?: string) => eventDispatch('retorik_resumeConversation', [], instanceId)
const sendMessageToChatbot = (value: string, instanceId?: string) => eventDispatch('retorik_sendMessageToChatbot', [value], instanceId)
const addExternalActivityHandler = (value: (action: any) => boolean, instanceId?: string) => eventDispatch('retorik_addExternalActivityHandler', [value], instanceId)
const setExternalActivityHandlers = (value: Array<(action: any) => boolean>, instanceId?: string) => eventDispatch('retorik_setExternalActivityHandlers', [value], instanceId)
const resetDirectlineStore = (instanceId?: string) => eventDispatch('retorik_resetDirectlineStore', [], instanceId)

export {
  openWidget,
  closeWidget,
  toggleWidget,
  toggleDisplay,
  showAgent,
  hideAgent,
  resetUtilsStore,
  lockClickedButton,
  setCurrentCustomView,
  setCurrentSubView,
  sendActivity,
  addDetailedAttachmentTrigger,
  setModalFullscreenContent,
  setDashboardOpened,
  setLocale,
  resetLocaleStore,
  addDumpedLanguageFallback,
  addReplacementLocale,
  setValue,
  setCurrentId,
  setToggleValue,
  addCurrentToggleId,
  resetAdaptiveCardStore,
  setVisibility,
  toggleVisibility,
  setActiveRecognitionState,
  cancelCurrentUtterance,
  toggleMicrophone,
  setMuted,
  playExternalSynthesis,
  resetSpeechStore,
  setDisplaySubtitles,
  toggleSubtitles,
  setAgentData,
  setMode,
  setLoaderClosed,
  resetRetorikStore,
  resetActivityStore,
  sendMessage,
  sendEvent,
  pauseConversation,
  resumeConversation,
  sendMessageToChatbot,
  addExternalActivityHandler,
  setExternalActivityHandlers,
  resetDirectlineStore
}
